/**
 * Person journal presentation (admin console). Pure functions only, so the
 * rules that matter are testable without a DOM: how a day is grouped, what a
 * kind is called, and above all what happens to a value nobody measured.
 *
 * The server sends null for anything it did not measure. That null must reach
 * the screen as the words "not measured" and never as a 0, a dash or a blank
 * cell that reads like a real low reading. This screen exists so Brian can
 * trust what he sees about one person before he tells Elina how to answer them.
 */

export const JOURNAL_KINDS = [
  'daily_check',
  'rom_session',
  'exercise',
  'active_session',
  'active_cue',
  'reading_rollup',
  'elina_message',
  'elina_knowledge',
  'elina_email',
  'reflection',
  'mode_change',
  'calendar_busy_day',
  'support_ticket',
  'session_trace',
  // The felt-worse follow-up (pv-auth #469) and the safety stops and
  // readiness answers (pv-auth #480, invariant 88): words-free lines.
  'felt_worse',
  'safety',
  'payment',
] as const;

export type JournalKind = (typeof JOURNAL_KINDS)[number];

export const KIND_LABEL: Record<JournalKind, string> = {
  daily_check: 'Daily check',
  rom_session: 'Range of motion',
  exercise: 'Exercise',
  active_session: 'Active Mode',
  active_cue: 'Cue',
  reading_rollup: 'Readings by hour',
  elina_message: 'Elina chat',
  elina_knowledge: 'Elina learned',
  elina_email: 'Elina email',
  reflection: 'Reflection',
  mode_change: 'Season',
  calendar_busy_day: 'Calendar',
  support_ticket: 'Support',
  session_trace: 'Session',
  felt_worse: 'Felt worse',
  safety: 'Safety stop',
  payment: 'Payment',
};

/** One glyph per kind, so a long timeline can be skimmed down the left edge. */
export const KIND_ICON: Record<JournalKind, string> = {
  daily_check: '◎',
  rom_session: '↻',
  exercise: '▲',
  active_session: '▮',
  active_cue: '!',
  reading_rollup: '∿',
  elina_message: '❝',
  elina_knowledge: '✦',
  elina_email: '✉',
  reflection: '✎',
  mode_change: '◆',
  calendar_busy_day: '▦',
  support_ticket: '?',
  session_trace: '⧗',
  felt_worse: '▼',
  safety: '✚',
  payment: '$',
};

export interface JournalEntry {
  id: string;
  at: string;
  kind: JournalKind;
  summary: string;
  data: Record<string, unknown>;
}

export interface JournalNote {
  id: string;
  body: string;
  authorEmail: string | null;
  createdAt: string;
}

export interface JournalPage {
  userId: string;
  person: { email: string; name: string; createdAt: string; timezone: string | null } | null;
  entries: JournalEntry[];
  nextCursor: string | null;
  unavailable: { kind: JournalKind; reason: string }[];
  notes: JournalNote[];
}

export const isJournalKind = (v: unknown): v is JournalKind =>
  typeof v === 'string' && (JOURNAL_KINDS as readonly string[]).includes(v);

/** The words for a value that was never taken. Never a number, never a blank. */
export const NOT_MEASURED = 'not measured';

/**
 * One field of an entry's data, ready to render. Null and undefined become the
 * words; everything else is shown as it arrived, with objects kept as JSON so
 * nothing is quietly rounded or dropped on the way to the screen.
 */
export const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return NOT_MEASURED;
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : NOT_MEASURED;
  if (typeof v === 'string') return v.trim() ? v : NOT_MEASURED;
  if (Array.isArray(v)) return v.length ? JSON.stringify(v) : 'none';
  return JSON.stringify(v);
};

/**
 * camelCase to something readable, without a dictionary to keep in step with
 * the server. Sentence case, not title case: only the first word is capitalised,
 * so a long field list reads as prose rather than as a row of headings.
 */
export const labelForKey = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, (_m, a: string, b: string) => `${a} ${b.toLowerCase()}`)
    .replace(/_+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export interface DataRow {
  key: string;
  label: string;
  value: string;
  /** True when the source had nothing here, so the row can be styled as absent. */
  missing: boolean;
}

/** Every field of an entry, in the order the server sent them. */
export const dataRows = (data: Record<string, unknown>): DataRow[] =>
  Object.entries(data ?? {}).map(([key, v]) => ({
    key,
    label: labelForKey(key),
    value: formatValue(v),
    missing: v === null || v === undefined,
  }));

export interface JournalDay {
  /** YYYY-MM-DD in the viewer's own zone, which is the zone the heading shows. */
  day: string;
  label: string;
  entries: JournalEntry[];
}

const dayKey = (at: string): string => {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const dayLabel = (day: string): string => {
  if (day === 'unknown') return 'Date not recorded';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return 'Date not recorded';
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

/**
 * Group a timeline into days, keeping the order it arrived in. The entries are
 * already newest first, so the groups come out newest first too, and no entry
 * is ever reordered inside its day.
 */
export const groupByDay = (entries: JournalEntry[]): JournalDay[] => {
  const out: JournalDay[] = [];
  for (const e of entries) {
    const day = dayKey(e.at);
    const last = out[out.length - 1];
    if (last && last.day === day) last.entries.push(e);
    else out.push({ day, label: dayLabel(day), entries: [e] });
  }
  return out;
};

/** The clock time on one entry's row. */
export const timeOf = (at: string): string => {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? 'time not recorded' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/** How many of each kind are on the page, for the filter row's counts. */
export const countByKind = (entries: JournalEntry[]): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const e of entries) out[e.kind] = (out[e.kind] ?? 0) + 1;
  return out;
};
